/**
 * Target acquisition.
 *
 * Two jobs. `EnemyIndex` keeps a spatial hash over the live enemy array, rebuilt
 * once per sim step and shared by every tower and every projectile that needs a
 * radius query. `selectTarget` walks the candidates inside one tower's range and
 * returns the winning enemy index under the tower's `TargetingMode`.
 *
 * ## Allocation
 *
 * Zero, in steady state. The `SpatialHash` API takes callbacks, and a callback
 * written inline would allocate a closure every call, so every closure this file
 * needs is built once in the constructor and holds its arguments in fields. The
 * result is uglier than a lambda and it is the difference between a smooth frame
 * and a garbage pause every few seconds.
 *
 * ## Tie-breaking
 *
 * Every mode falls back to higher `progress` on a tie. Without it, two enemies
 * with identical health flicker back and forth as the float comparison lands
 * differently frame to frame, the barrel jitters, and pierce shots miss. Track
 * progress is unique enough in practice and monotonic in time, so the choice is
 * stable across steps even as both candidates move.
 *
 * ## Effective health
 *
 * `strongest` and `weakest` read `hp + shield`, not `hp`. A shielded enemy at
 * 1 hp behind a full overshield is not weak, and a tower told to finish off the
 * weakest target should not waste a round chewing through 400 points of shield.
 */

import { VIEW } from '@/core/config';
import { SpatialHash } from '@/core/spatial';
import type { Enemy, TargetingMode, Tower } from '@/core/types';

/** Cell edge for the enemy hash. Roughly the median tower range over four. */
const CELL = 32;

/** How deeply sweeps may nest before further ones are dropped. */
export const MAX_SWEEP_DEPTH = 4;

/** Sentinel for "no target". */
export const NO_TARGET = -1;

export class EnemyIndex {
  readonly hash: SpatialHash;

  /** Live view of the world's enemy array. Rebuilt reference each step. */
  private enemies: Enemy[] = [];
  private count = 0;

  // Pre-bound callbacks. Built once; the hash calls them thousands of times.
  private readonly getX: (i: number) => number;
  private readonly getY: (i: number) => number;
  private readonly visitSelect: (i: number) => void;
  private readonly visitRadius: (i: number) => void;

  // Query state for `selectTarget`.
  private qx = 0;
  private qy = 0;
  private qr2 = 0;
  private qMode: TargetingMode = 'first';
  private best = NO_TARGET;
  private bestScore = 0;
  private bestProgress = 0;
  private bestId = 0;

  // Query state for `forEachInRadius`.
  private skipId = -1;
  /**
   * Gather buffers, one pair per nesting depth.
   *
   * A sweep callback routinely starts another sweep — a splash kills an enemy,
   * which spreads a DOT, which sweeps again. Walking the hash while a nested
   * walk mutates the shared query fields would corrupt both. So a sweep gathers
   * its candidates first, releases the hash, and only then runs the callback,
   * with the candidate list held at its own depth.
   */
  private readonly gatherIdx: Int32Array[] = [];
  private readonly gatherDist: Float32Array[] = [];
  private gatherCount = 0;
  private depth = 0;

  constructor(capacity = 4096) {
    this.hash = new SpatialHash(VIEW.width, VIEW.height, CELL, capacity);
    this.getX = (i) => this.enemies[i].pos.x;
    this.getY = (i) => this.enemies[i].pos.y;
    this.visitSelect = (i) => this.considerTarget(i);
    this.visitRadius = (i) => this.considerRadius(i);
    for (let d = 0; d < MAX_SWEEP_DEPTH; d++) {
      this.gatherIdx.push(new Int32Array(256));
      this.gatherDist.push(new Float32Array(256));
    }
  }

  /**
   * Id lookup for anything holding a reference across steps — homing
   * projectiles mostly. Rebuilt with the hash so the two never disagree.
   */
  readonly byId = new Map<number, number>();

  /** Call once per sim step, before any tower or projectile queries. */
  rebuild(enemies: Enemy[]): void {
    this.enemies = enemies;
    this.count = enemies.length;
    this.hash.rebuild(this.count, this.getX, this.getY);
    this.byId.clear();
    for (let i = 0; i < this.count; i++) this.byId.set(enemies[i].id, i);
  }

  get list(): Enemy[] { return this.enemies; }

  /** Live enemy with this id, or `undefined` if it died or never existed. */
  find(id: number): Enemy | undefined {
    const i = this.byId.get(id);
    if (i === undefined) return undefined;
    const e = this.enemies[i];
    return e && e.alive ? e : undefined;
  }

  // ────────────────────────────────────────────────────────── target choice ──

  /**
   * Returns the index into the enemy array of the tower's target, or
   * {@link NO_TARGET}. `rangeOverride` lets a caller widen the search without
   * mutating the tower — the sniper's `global` grant passes the map diagonal.
   */
  selectTarget(tower: Tower, mode: TargetingMode, rangeOverride = 0): number {
    const range = rangeOverride > 0 ? rangeOverride : tower.stats.range;
    if (range <= 0 || this.count === 0) return NO_TARGET;

    this.qx = tower.pos.x;
    this.qy = tower.pos.y;
    this.qr2 = range * range;
    this.qMode = mode;
    this.best = NO_TARGET;
    this.bestProgress = -Infinity;
    // Seeded so the first candidate always wins its own comparison.
    this.bestScore = (mode === 'last' || mode === 'weakest' || mode === 'closest')
      ? Infinity : -Infinity;

    this.hash.query(this.qx, this.qy, range, this.visitSelect);
    return this.best;
  }

  private considerTarget(i: number): void {
    const e = this.enemies[i];
    if (!e || !e.alive) return;

    const dx = e.pos.x - this.qx;
    const dy = e.pos.y - this.qy;
    const d2 = dx * dx + dy * dy;
    // The hash hands back whole cells, so the exact circle test lives here.
    if (d2 > this.qr2) return;

    let score: number;
    let higherWins: boolean;
    switch (this.qMode) {
      case 'first': score = e.progress; higherWins = true; break;
      case 'last': score = e.progress; higherWins = false; break;
      case 'strongest': score = e.hp + e.shield; higherWins = true; break;
      case 'weakest': score = e.hp + e.shield; higherWins = false; break;
      case 'closest': score = d2; higherWins = false; break;
      default: score = d2; higherWins = true; break; // 'furthest'
    }

    if (this.best === NO_TARGET) {
      this.best = i; this.bestScore = score; this.bestProgress = e.progress; this.bestId = e.id;
      return;
    }
    if (score === this.bestScore) {
      // Stable tie-break: whichever is deeper along the track, then by id.
      //
      // The id fallback matters more than it looks. The spatial hash returns
      // candidates in cell order, which changes as enemies move, so without a
      // total ordering a tower facing two identical targets reassigns every
      // few steps, the barrel jitters, and its damage is split across targets
      // that both survive. Ids are unique and immutable, so this terminates.
      if (e.progress > this.bestProgress
        || (e.progress === this.bestProgress && e.id < this.bestId)) {
        this.best = i; this.bestProgress = e.progress; this.bestId = e.id;
      }
      return;
    }
    if (higherWins ? score > this.bestScore : score < this.bestScore) {
      this.best = i; this.bestScore = score; this.bestProgress = e.progress; this.bestId = e.id;
    }
  }

  // ─────────────────────────────────────────────────────────── radius sweep ──

  /**
   * Visits every live enemy within `radius`, exact circle test applied.
   *
   * Re-entrant up to {@link MAX_SWEEP_DEPTH} levels: the candidates are gathered
   * before the first callback runs, so a callback is free to start its own
   * sweep. `skipId` drops one enemy by id, which is how chain lightning and
   * spread-on-death avoid re-hitting the thing they came from.
   *
   * `cb` should be a pre-bound method, not an inline lambda — this runs several
   * thousand times a second.
   */
  forEachInRadius(
    x: number, y: number, radius: number,
    cb: (enemy: Enemy, index: number, dist2: number) => void,
    skipId = -1,
  ): void {
    if (radius <= 0 || this.count === 0) return;
    if (this.depth >= MAX_SWEEP_DEPTH) return;

    const level = this.depth++;
    this.qx = x; this.qy = y; this.qr2 = radius * radius;
    this.skipId = skipId;
    this.gatherCount = 0;
    this.hash.query(x, y, radius, this.visitRadius);

    const n = this.gatherCount;
    const idx = this.gatherIdx[level];
    const dist = this.gatherDist[level];
    for (let k = 0; k < n; k++) {
      const e = this.enemies[idx[k]];
      // Re-checked: an earlier callback in this same sweep may have killed it.
      if (e && e.alive) cb(e, idx[k], dist[k]);
    }
    this.depth--;
  }

  private considerRadius(i: number): void {
    const e = this.enemies[i];
    if (!e || !e.alive || e.id === this.skipId) return;
    const dx = e.pos.x - this.qx;
    const dy = e.pos.y - this.qy;
    const d2 = dx * dx + dy * dy;
    if (d2 > this.qr2) return;

    const level = this.depth - 1;
    let idx = this.gatherIdx[level];
    if (this.gatherCount >= idx.length) {
      const bigger = new Int32Array(idx.length * 2);
      const biggerD = new Float32Array(idx.length * 2);
      bigger.set(idx); biggerD.set(this.gatherDist[level]);
      this.gatherIdx[level] = bigger;
      this.gatherDist[level] = biggerD;
      idx = bigger;
    }
    idx[this.gatherCount] = i;
    this.gatherDist[level][this.gatherCount] = d2;
    this.gatherCount++;
  }

  /** Nearest live enemy to a point, or {@link NO_TARGET}. Used by chain jumps. */
  nearest(x: number, y: number, radius: number, skipId = -1): number {
    if (radius <= 0 || this.count === 0) return NO_TARGET;
    this.qx = x; this.qy = y; this.qr2 = radius * radius;
    this.qMode = 'closest';
    this.skipIdForSelect = skipId;
    this.best = NO_TARGET;
    this.bestScore = Infinity;
    this.bestProgress = -Infinity;
    this.bestId = Infinity;
    this.hash.query(x, y, radius, this.visitNearest);
    this.skipIdForSelect = -1;
    return this.best;
  }

  private skipIdForSelect = -1;
  private readonly visitNearest = (i: number): void => {
    const e = this.enemies[i];
    if (!e || !e.alive || e.id === this.skipIdForSelect) return;
    const dx = e.pos.x - this.qx;
    const dy = e.pos.y - this.qy;
    const d2 = dx * dx + dy * dy;
    if (d2 > this.qr2) return;
    if (d2 < this.bestScore
      || (d2 === this.bestScore && e.progress > this.bestProgress)
      || (d2 === this.bestScore && e.progress === this.bestProgress && e.id < this.bestId)) {
      this.best = i; this.bestScore = d2; this.bestProgress = e.progress; this.bestId = e.id;
    }
  };
}

/** Every mode, in the order the targeting toggle cycles them. */
export const TARGETING_MODES: readonly TargetingMode[] = [
  'first', 'last', 'strongest', 'weakest', 'closest', 'furthest',
];

/** Short labels for the tower detail panel. */
export const TARGETING_LABELS: Readonly<Record<TargetingMode, string>> = {
  first: 'First',
  last: 'Last',
  strongest: 'Strong',
  weakest: 'Weak',
  closest: 'Close',
  furthest: 'Far',
};

export function cycleTargeting(mode: TargetingMode, dir = 1): TargetingMode {
  const i = TARGETING_MODES.indexOf(mode);
  const n = TARGETING_MODES.length;
  return TARGETING_MODES[((i + dir) % n + n) % n];
}
